import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Bot,
  User,
  Code,
  Loader2,
  MessageSquare,
  Clock,
  Trash2,
  ArrowLeft,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileText,
  Presentation,
  Copy,
  CheckCircle2,
  ExternalLink,
  Download,
  Search,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import DataTable from '../components/DataTable';
import AssetGrid from '../components/AssetGrid';
import MessageVoting from '../components/MessageVoting';
import FeedbackModal from '../components/FeedbackModal';

function parseMessageMetadata(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/** Chat History: infer PDF vs Word for Contract Redline artifact (metadata + filename). */
function redlinedArtifactKind(m) {
  const name = (m.redlinedFilename || '').toLowerCase();
  const mime = (m.redlinedMimeType || '').toLowerCase();
  if (m.redlinedFormat === 'word') return { isWord: true, label: 'Word document', defaultFile: 'redlined.docx' };
  if (m.redlinedFormat === 'pdf') return { isWord: false, label: 'PDF', defaultFile: 'redlined.pdf' };
  if (
    name.endsWith('.docx') ||
    name.endsWith('.doc') ||
    mime.includes('wordprocessing') ||
    mime.includes('msword')
  ) {
    return { isWord: true, label: 'Word document', defaultFile: name.endsWith('.doc') ? 'redlined.doc' : 'redlined.docx' };
  }
  return { isWord: false, label: 'PDF', defaultFile: 'redlined.pdf' };
}

/** Unify DB rows (role), AI Answer payloads (type), and flattened metadata fields. */
function normalizeHistoryMessage(message) {
  const meta = parseMessageMetadata(message.metadata);
  const role =
    message.role ??
    (message.type === 'user' ? 'user' : message.type === 'bot' ? 'assistant' : 'assistant');
  const sqlQuery = message.sqlQuery ?? meta.sqlQuery ?? null;
  const data = message.data ?? meta.data ?? null;
  const columns = message.columns ?? meta.columns ?? null;
  const assetUrls = message.assetUrls ?? meta.assetUrls ?? null;
  const joboticsSteps =
    meta.steps ?? (meta.jobotics && Array.isArray(meta.jobotics.steps) ? meta.jobotics.steps : null);
  return {
    ...message,
    role,
    metadata: meta,
    sqlQuery,
    data,
    columns,
    assetUrls,
    joboticsSteps: Array.isArray(joboticsSteps) ? joboticsSteps : null,
    redlinedArtifact: meta.redlinedArtifact === true,
    redlinedFilename: meta.redlinedFilename || null,
    redlinedMimeType: meta.redlinedMimeType || null,
    redlinedFormat: meta.redlinedFormat === 'pdf' || meta.redlinedFormat === 'word' ? meta.redlinedFormat : null,
  };
}

const UserChat = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [userVotes, setUserVotes] = useState({});
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageId: null });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalSessions: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [historySearchInput, setHistorySearchInput] = useState('');
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState('');
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/voice-agent');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedHistorySearch(historySearchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [historySearchInput]);

  const loadSessions = useCallback(async (page = 1) => {
    try {
      setSessionsLoading(true);
      const q = debouncedHistorySearch;
      const searchParam = q ? `&search=${encodeURIComponent(q)}` : '';
      const response = await axios.get(
        `/api/user-chat/sessions?page=${page}&limit=20&scope=demo${searchParam}`
      );
      if (response.data.success) {
        const rows = response.data.sessions;
        setSessions(rows);
        setPagination(response.data.pagination);
        setCurrentSession((prev) => {
          if (!rows.length) return null;
          if (prev && rows.some((s) => s.id === prev.id)) return prev;
          return rows[0];
        });
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load chat sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, [debouncedHistorySearch]);

  useEffect(() => {
    loadSessions(1);
  }, [loadSessions]);

  // Load messages for current session
  const loadMessages = async (sessionId) => {
    try {
      const response = await axios.get(`/api/user-chat/sessions/${sessionId}/messages`);
      if (response.data.success) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load chat history');
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (pagination.hasPrevPage) {
      loadSessions(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      loadSessions(pagination.currentPage + 1);
    }
  };

  const handlePageChange = (newPage) => {
    loadSessions(newPage);
  };

  useEffect(() => {
    if (currentSession) {
      loadMessages(currentSession.id);
    }
  }, [currentSession]);

  const filteredMessages = useMemo(() => {
    const q = debouncedHistorySearch.toLowerCase();
    if (!q) return messages;
    return messages.filter((msg) => {
      const raw = msg.content != null ? String(msg.content) : '';
      return raw.toLowerCase().includes(q);
    });
  }, [messages, debouncedHistorySearch]);

  useEffect(() => {
    scrollToBottom();
  }, [filteredMessages]);



  // Delete chat session
  const deleteSession = async (sessionId, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this chat session?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/user-chat/sessions/${sessionId}`);
      if (response.data.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (currentSession?.id === sessionId) {
          const remainingSessions = sessions.filter(s => s.id !== sessionId);
          setCurrentSession(remainingSessions.length > 0 ? remainingSessions[0] : null);
          setMessages([]);
        }
        toast.success('Chat session deleted');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete chat session');
    }
  };



  // Handle voting on messages
  const handleVote = async (messageId, vote) => {
    setUserVotes(prev => ({ ...prev, [messageId]: vote }));
    
    try {
      await axios.post('/api/feedback/vote', {
        messageId,
        vote,
        timestamp: new Date().toISOString()
      });
      
      if (vote === 'up') {
        toast.success('Thank you for your feedback!');
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  // Handle feedback for thumbs down
  const handleFeedback = (messageId) => {
    setFeedbackModal({ isOpen: true, messageId });
  };

  const contractRedlineNumericSessionId = useCallback((demoSessionId) => {
    if (!demoSessionId || typeof demoSessionId !== 'string') return null;
    if (!demoSessionId.startsWith('chat_')) return null;
    const n = demoSessionId.slice('chat_'.length);
    return /^\d+$/.test(n) ? n : null;
  }, []);

  const handleDownloadRedlinedArtifact = useCallback(async (numericSessionId, filename) => {
    if (!numericSessionId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`/api/contracts/redlined-artifact/${numericSessionId}`, {
        headers,
        responseType: 'blob',
        withCredentials: true,
      });
      const fname = filename || 'redlined.pdf';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      let msg = err.response?.data?.error || err.message || 'Download failed';
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          try {
            msg = JSON.parse(t).error || t;
          } catch {
            msg = t || msg;
          }
        } catch {
          msg = 'Download failed';
        }
      }
      toast.error(typeof msg === 'string' && msg ? msg : 'Download failed');
    }
  }, []);

  const handleDownloadSharePointFile = useCallback(async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`/api/project-ops/download-sharepoint?filename=${encodeURIComponent(filename)}`, {
        headers,
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      toast.error(`Download failed: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  const chatMarkdownComponents = useMemo(
    () => ({
      table: ({ children }) => (
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
      th: ({ children }) => (
        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b border-gray-200">{children}</th>
      ),
      td: ({ children }) => <td className="px-3 py-2 text-gray-700 border-b border-gray-100">{children}</td>,
      h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
      h3: ({ children }) => <h3 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
      strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
      ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
      li: ({ children }) => <li className="text-gray-700">{children}</li>,
      p: ({ children }) => <p className="my-1.5 leading-relaxed text-gray-800">{children}</p>,
      a: ({ href, children }) => {
        if (!href || href === '#') return <span className="text-indigo-600">{children}</span>;
        const isDownloadSP = href.includes('/api/project-ops/download-sharepoint');
        const isAgentDownload = href.includes('/api/jobotics-agent/download/');
        const isAgentView = href.includes('/api/jobotics-agent/view/');
        if (isDownloadSP) {
          const fname = decodeURIComponent(href.split('filename=')[1] || '');
          return (
            <button
              type="button"
              onClick={() => handleDownloadSharePointFile(fname)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white border border-indigo-700 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <FileText className="w-3.5 h-3.5 shrink-0" />
              {children || 'Download'}
            </button>
          );
        }
        if (isAgentDownload) {
          return (
            <a
              href={href}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white border border-emerald-700 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm no-underline"
            >
              <FileText className="w-3.5 h-3.5 shrink-0" />
              {children || 'Download file'}
            </a>
          );
        }
        if (isAgentView) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white border border-blue-700 rounded-lg hover:bg-blue-700 transition-colors shadow-sm no-underline"
            >
              <Presentation className="w-3.5 h-3.5 shrink-0" />
              {children || 'Open'}
            </a>
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium underline underline-offset-2"
          >
            {children}
            <ExternalLink className="w-3 h-3 opacity-70 shrink-0" />
          </a>
        );
      },
      code: ({ inline, children }) =>
        inline ? (
          <code className="px-1.5 py-0.5 bg-gray-100 text-indigo-800 rounded text-xs font-mono">{children}</code>
        ) : (
          <pre className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-xs font-mono my-2">
            <code>{children}</code>
          </pre>
        ),
    }),
    [handleDownloadSharePointFile]
  );

  const copyToClipboard = useCallback((text, label) => {
    if (text == null || text === '') return;
    navigator.clipboard.writeText(String(text)).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Could not copy')
    );
  }, []);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const demoSessionTypeLabel = (session) => {
    const t = session.session_type;
    if (t === 'jobotics_agent') return 'Jobotics Agent';
    if (t === 'voice_pm') return 'PM Voice';
    if (t === 'contract_review' || t === 'contract_redline') return 'Contract Redline';
    if (t === 'general') return 'Chat';
    return t ? String(t).replace(/_/g, ' ') : 'Chat';
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar with chat sessions */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Main"
              >
                <ArrowLeft size={16} />
              </button>
              <h2 className="text-lg font-semibold text-gray-900">Chat History</h2>
            </div>
            <button
              onClick={() => {
                toast.success('Refreshing chat history...');
                loadSessions(pagination.currentPage);
              }}
              disabled={sessionsLoading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh chat history"
            >
              <RefreshCw size={16} className={sessionsLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mb-3 leading-snug pr-2">
            Demo focus: Jobotics Agent, PM Voice, Contract Redline (saved threads), and general chat. Vista / analyst threads are hidden here.
          </p>
          {user && (
            <div className="text-sm text-gray-600">
              Welcome back, {user.name || user.username}!
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <label htmlFor="chat-history-search" className="sr-only">
              Search chat history
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                id="chat-history-search"
                type="search"
                value={historySearchInput}
                onChange={(e) => setHistorySearchInput(e.target.value)}
                placeholder="Search titles & messages…"
                autoComplete="off"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              {historySearchInput ? (
                <button
                  type="button"
                  onClick={() => setHistorySearchInput('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <p className="mt-1.5 text-[10px] leading-snug text-gray-400">
              Keyword match on session titles and message text (case-insensitive). Sidebar lists matching sessions;
              open one to filter messages here too.
            </p>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {sessionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-gray-400" size={24} />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 px-2">
              <Search className="mx-auto mb-2 opacity-40" size={40} />
              <p className="text-sm">
                {debouncedHistorySearch
                  ? `No sessions match “${debouncedHistorySearch}”. Try different keywords.`
                  : 'No chat history available'}
              </p>
            </div>
          ) : (
            <>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`relative rounded-xl border mb-2 transition-colors group ${
                    currentSession?.id === session.id
                      ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200'
                      : 'bg-white border-gray-200 hover:border-indigo-200 hover:bg-gray-50/80'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setCurrentSession(session)}
                    className="w-full text-left p-3 pr-10 rounded-xl cursor-pointer"
                  >
                    <h3 className="font-medium text-sm text-gray-900 truncate pr-1">
                      {session.title || 'Untitled Chat'}
                    </h3>
                    <span className="inline-flex mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-800 border border-indigo-200">
                      {demoSessionTypeLabel(session)}
                    </span>
                    <div className="flex items-center mt-2 text-xs text-gray-500">
                      <Clock size={12} className="mr-1 shrink-0" />
                      {formatTimestamp(session.updated_at)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => deleteSession(session.id, e)}
                    className="absolute top-2 right-2 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-70 group-hover:opacity-100 transition-all"
                    title="Delete session"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={handlePrevPage}
                    disabled={!pagination.hasPrevPage}
                    className="flex items-center px-3 py-1 text-sm text-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed hover:text-gray-900 transition-colors"
                  >
                    <ChevronLeft size={16} className="mr-1" />
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({pagination.totalSessions} total)
                    </span>
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={!pagination.hasNextPage}
                    className="flex items-center px-3 py-1 text-sm text-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed hover:text-gray-900 transition-colors"
                  >
                    Next
                    <ChevronRight size={16} className="ml-1" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {currentSession ? (
          <>
            {/* Chat header */}
            <div className="bg-white border-b border-gray-200 p-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-gray-900 truncate">
                  {currentSession.title || 'Chat Session'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Session from {formatTimestamp(currentSession.created_at)} · Updated {formatTimestamp(currentSession.updated_at)}
                </p>
                <span className="inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                  {demoSessionTypeLabel(currentSession)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link
                  to="/jobotics-agent"
                  className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 shadow-sm"
                >
                  Jobotics Agent
                </Link>
                <Link
                  to="/voice-agent"
                  className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 shadow-sm"
                >
                  PM Voice
                </Link>
                <Link
                  to="/contract-redline"
                  className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-900 hover:bg-amber-50 shadow-sm"
                >
                  Contract Redline
                </Link>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <Bot className="mx-auto mb-4 text-gray-400" size={48} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No messages in this session</h3>
                  <p className="text-gray-600">This chat session doesn't contain any conversation history.</p>
                </div>
              )}

              {messages.length > 0 &&
                filteredMessages.length === 0 &&
                debouncedHistorySearch && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                    No messages in this conversation match{' '}
                    <span className="font-semibold">“{debouncedHistorySearch}”</span>. Try another session from the
                    sidebar or clear the search.
                  </div>
                )}

              {filteredMessages.map((message, index) => {
                const m = normalizeHistoryMessage(message);
                const redlineKind = redlinedArtifactKind(m);
                const content = m.content != null ? String(m.content) : '';
                return (
                <div key={m.id || index} className="flex space-x-3">
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                    m.role === 'user'
                      ? 'bg-sylvan-600 text-white'
                      : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                  }`}>
                    {m.role === 'user' ? <User size={16} /> : <Bot size={16} />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className={`rounded-xl p-4 shadow-sm ${
                      m.role === 'user'
                        ? 'bg-sylvan-50 border border-sylvan-200'
                        : 'bg-white border border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          {m.role === 'user' ? 'You' : 'Assistant'}
                        </span>
                        {m.timestamp && (
                          <span className="text-[11px] text-gray-400">{formatTimestamp(m.timestamp)}</span>
                        )}
                      </div>

                      {m.role === 'user' ? (
                        <div className="prose prose-sm max-w-none text-gray-900">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                            {content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none text-gray-900">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                            {content || '*No content*'}
                          </ReactMarkdown>
                        </div>
                      )}

                      {m.joboticsSteps && m.joboticsSteps.length > 0 && (
                        <details className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/50 group">
                          <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-semibold text-indigo-900 hover:bg-indigo-100/80 rounded-lg">
                            <ChevronDown className="w-4 h-4 shrink-0 transition-transform group-open:rotate-180" />
                            <span>Execution steps ({m.joboticsSteps.length})</span>
                          </summary>
                          <ul className="px-3 pb-3 pt-1 space-y-1.5 border-t border-indigo-100">
                            {m.joboticsSteps.map((step, si) => (
                              <li key={step.id || si} className="flex items-start gap-2 text-xs text-gray-700">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                <span>{step.label || step.tool || 'Step'}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}

                      {m.sqlQuery && (
                        <div className="mt-4 p-3 bg-gray-900 rounded-lg border border-gray-700">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center text-gray-300">
                              <Code size={14} className="mr-2 shrink-0" />
                              <span className="text-xs font-semibold uppercase tracking-wide">SQL</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(m.sqlQuery, 'SQL')}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-gray-700 text-white hover:bg-gray-600"
                            >
                              <Copy className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="text-xs text-green-400 overflow-x-auto font-mono leading-relaxed">{m.sqlQuery}</pre>
                        </div>
                      )}

                      {m.data && m.data.length > 0 && (
                        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                          <DataTable
                            data={m.data}
                            columns={m.columns || Object.keys(m.data[0])}
                          />
                        </div>
                      )}

                      {m.assetUrls && m.assetUrls.length > 0 && (
                        <div className="mt-4">
                          <AssetGrid assetUrls={m.assetUrls} />
                        </div>
                      )}

                      {m.role === 'assistant' &&
                        m.redlinedArtifact &&
                        currentSession.session_type === 'contract_redline' &&
                        contractRedlineNumericSessionId(currentSession.id) && (
                          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-3 space-y-2">
                            <p className="text-xs font-semibold text-amber-950">
                              Redlined {redlineKind.label}
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadRedlinedArtifact(
                                  contractRedlineNumericSessionId(currentSession.id),
                                  m.redlinedFilename || redlineKind.defaultFile
                                )
                              }
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-600 text-white border border-amber-700 hover:bg-amber-700 shadow-sm"
                            >
                              <Download className="w-4 h-4 shrink-0" />
                              {redlineKind.isWord
                                ? `Download redlined Word file (${m.redlinedFilename || redlineKind.defaultFile})`
                                : `Download redlined PDF (${m.redlinedFilename || redlineKind.defaultFile})`}
                            </button>
                            <p className="text-[11px] text-amber-900/80 leading-snug">
                              Same file via API (session cookie or Bearer; returns{' '}
                              {redlineKind.isWord ? 'DOCX/DOC' : 'PDF'}):{' '}
                              <code className="bg-amber-100/90 px-1.5 py-0.5 rounded text-[10px]">
                                /api/contracts/redlined-artifact/
                                {contractRedlineNumericSessionId(currentSession.id)}
                              </code>
                            </p>
                          </div>
                        )}

                      {m.role === 'assistant' && content.length > 400 && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(content, 'Message')}
                          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 bg-gray-50 text-gray-800 hover:bg-gray-100"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copy full reply
                        </button>
                      )}
                    </div>

                    {m.role === 'assistant' && (
                      <div className="mt-2">
                        <MessageVoting
                          messageId={m.id}
                          onVote={handleVote}
                          onFeedback={handleFeedback}
                          userVote={userVotes[m.id]}
                        />
                      </div>
                    )}
                  </div>
                </div>
                );
              })}

              
              <div ref={messagesEndRef} />
            </div>


          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="mx-auto mb-4 text-gray-400" size={64} />
              <h3 className="text-xl font-medium text-gray-900 mb-2">Chat History</h3>
              <p className="text-gray-600">Select a chat session from the sidebar to view your conversation history.</p>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, messageId: null })}
        messageId={feedbackModal.messageId}
      />
    </div>
  );
};

export default UserChat;